/**
 * Liste des Clients - Vue principale
 * Source : customerOrgs. Tri/filtre serveur, recherche client-side, pagination.
 */

import React from 'react';
import {
    Search,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Users,
    ChevronRight,
    Download,
    ChevronLeft,
} from 'lucide-react';
import { Tooltip } from '@mui/material';
import { Card, CardBody } from '@/components/ui';
import { CustomerStatusDisplay } from './CustomerBadge';
import {
    CustomerOrgsSortField,
    CustomerOrgsFilter,
    CUSTOMER_STATUS_CONFIG,
    type CustomersListProps,
} from './types';
import {
    filterCustomerOrgsBySearch,
    derivePeriodStatusFromCustomerOrg,
    formatCurrency,
    formatDate,
    formatRelativeDate,
    formatPurchaseFrequency,
    getPeriodStartDate,
} from './customerUtils';

export const CustomersList: React.FC<CustomersListProps> = ({
    customers,
    loading,
    onSelectCustomer,
    selectedCustomerId,
    period,
    sort,
    onSortChange,
    filter,
    onFilterChange,
    hasMore,
    onLoadMore,
    pageLimit,
    onPageLimitChange,
    search,
    onSearchChange,
    totalCount,
    inactiveCount,
    currentPage = 1,
    totalPages = 1,
    onPageChange,
}) => {
    const periodStart = React.useMemo(() => getPeriodStartDate(period), [period]);
    const filtered = filterCustomerOrgsBySearch(customers, search);

    const handleSort = (field: CustomerOrgsSortField) => {
        onSortChange(field, sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc');
    };

    const getSortIcon = (columnId: string) => {
        if (sort.field !== columnId) return <ArrowUpDown className="h-4 w-4 text-foreground-secondary opacity-50" />;
        return sort.direction === 'desc' ? <ArrowDown className="h-4 w-4 text-primary" /> : <ArrowUp className="h-4 w-4 text-primary" />;
    };

    const handleExportCSV = () => {
        const headers = ['Nom', 'Email', 'TikTok', 'Téléphone', 'Statut', 'Première commande', 'Dernière commande', 'Commandes', 'CA total', 'Panier moyen', 'Fréquence'];
        const rows = filtered.map((c) => {
            const st = derivePeriodStatusFromCustomerOrg(c, periodStart);
            return [
                c.fullName,
                c.email,
                c.tiktokUsername ?? '',
                c.phone ?? '',
                CUSTOMER_STATUS_CONFIG[st].label,
                c.firstPaidAt ? formatDate(c.firstPaidAt) : '',
                c.lastPaidAt ? formatDate(c.lastPaidAt) : '',
                c.ordersPaidCount.toString(),
                (c.totalSpentCents / 100).toFixed(2),
                c.ordersPaidCount ? (c.totalSpentCents / 100 / c.ordersPaidCount).toFixed(2) : '0',
                formatPurchaseFrequency(c.avgDaysBetweenPurchaseDays || undefined),
            ];
        });
        const csv = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `clients_${period}j_${formatDate(new Date()).replace(/\//g, '-')}.csv`;
        link.click();
    };

    if (loading && filtered.length === 0) {
        return (
            <Card>
                <CardBody>
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <span className="ml-3 text-foreground-secondary">Chargement des clients...</span>
                    </div>
                </CardBody>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            <Card>
                <CardBody className="p-4">
                    <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground-secondary" />
                            <input
                                type="text"
                                placeholder="Rechercher par nom, email, TikTok ou téléphone..."
                                value={search}
                                onChange={(e) => onSearchChange(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-surface text-foreground placeholder:text-foreground-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            {(['all', 'inactive'] as CustomerOrgsFilter[]).map((f) => {
                                const isActive = filter === f;
                                const count = f === 'all' ? totalCount : inactiveCount;
                                return (
                                    <Tooltip key={f} title={f === 'inactive' ? 'Pas d\'achat depuis la période' : 'Tous'} arrow>
                                        <button
                                            onClick={() => onFilterChange(f)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-all duration-200 ${isActive ? 'bg-primary-10 text-primary border-primary-20' : 'bg-background-tertiary text-foreground-secondary border-border hover:bg-surface'
                                                }`}
                                        >
                                            <span>{f === 'inactive' ? 'Inactifs' : 'Tous'}</span>
                                            <span className={`text-xs ${isActive ? 'opacity-80' : 'opacity-60'}`}>({count})</span>
                                        </button>
                                    </Tooltip>
                                );
                            })}
                        </div>
                        {onPageLimitChange && (
                            <div className="flex items-center gap-1 border border-border rounded-lg p-1 bg-background-tertiary">
                                <span className="text-xs text-foreground-secondary px-2">Par page</span>
                                {([25, 50] as const).map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => onPageLimitChange(n)}
                                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${pageLimit === n ? 'bg-primary text-white' : 'text-foreground-secondary hover:bg-surface'}`}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        )}
                        <Tooltip title="Exporter en CSV" arrow>
                            <button
                                onClick={handleExportCSV}
                                className="p-2 rounded-lg border border-border bg-background-tertiary hover:bg-surface transition-colors"
                            >
                                <Download className="h-4 w-4 text-foreground-secondary" />
                            </button>
                        </Tooltip>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-foreground-secondary">
                        <span className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            {(() => {
                                if (search) return `${filtered.length} client${filtered.length > 1 ? 's' : ''} (filtré)`;
                                if (totalCount > 0) return `Page ${currentPage} sur ${totalPages} · ${(currentPage - 1) * pageLimit + 1}–${(currentPage - 1) * pageLimit + filtered.length} sur ${totalCount}`;
                                return '0 client';
                            })()}
                        </span>
                        {onPageChange && totalPages > 1 && (
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => onPageChange(currentPage - 1)}
                                    disabled={currentPage <= 1 || loading}
                                    className="p-2 rounded-lg border border-border bg-background-tertiary hover:bg-surface disabled:opacity-50 disabled:pointer-events-none transition-colors"
                                    aria-label="Page précédente"
                                >
                                    <ChevronLeft className="h-4 w-4 text-foreground-secondary" />
                                </button>
                                <span className="px-2 text-xs font-medium text-foreground-secondary">
                                    {currentPage} / {totalPages}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => onPageChange(currentPage + 1)}
                                    disabled={currentPage >= totalPages || loading || !hasMore}
                                    className="p-2 rounded-lg border border-border bg-background-tertiary hover:bg-surface disabled:opacity-50 disabled:pointer-events-none transition-colors"
                                    aria-label="Page suivante"
                                >
                                    <ChevronRight className="h-4 w-4 text-foreground-secondary" />
                                </button>
                            </div>
                        )}
                    </div>
                </CardBody>
            </Card>

            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px]">
                        <thead>
                            <tr className="border-b border-border bg-background-tertiary">
                                <th className="px-4 py-3 text-xs font-semibold text-foreground-secondary uppercase tracking-wider text-left" style={{ width: '18%' }}>Client</th>
                                <th className="px-4 py-3 text-xs font-semibold text-foreground-secondary uppercase tracking-wider text-center" style={{ width: '10%' }}>Statut</th>
                                <th className="px-4 py-3 text-xs font-semibold text-foreground-secondary uppercase tracking-wider text-center" style={{ width: '12%' }}>Première commande</th>
                                <th className="px-4 py-3 text-xs font-semibold text-foreground-secondary uppercase tracking-wider text-center" style={{ width: '12%' }}>
                                    <button type="button" className="flex items-center gap-1.5 justify-center w-full hover:bg-surface transition-colors" onClick={() => handleSort('lastPaidAt')}>
                                        <span>Dernière commande</span>
                                        {getSortIcon('lastPaidAt')}
                                    </button>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-foreground-secondary uppercase tracking-wider text-center" style={{ width: '10%' }}>Commandes</th>
                                <th className="px-4 py-3 text-xs font-semibold text-foreground-secondary uppercase tracking-wider text-right" style={{ width: '12%' }}>
                                    <button type="button" className="flex items-center gap-1.5 justify-end w-full hover:bg-surface transition-colors" onClick={() => handleSort('totalSpentCents')}>
                                        <span>CA total</span>
                                        {getSortIcon('totalSpentCents')}
                                    </button>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-foreground-secondary uppercase tracking-wider text-right" style={{ width: '12%' }}>Panier moyen</th>
                                <th className="px-4 py-3 text-xs font-semibold text-foreground-secondary uppercase tracking-wider text-center" style={{ width: '14%' }}>Fréquence</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="px-4 py-12 text-center">
                                        <div className="flex flex-col items-center gap-2 text-foreground-secondary">
                                            <Users className="h-12 w-12 opacity-30" />
                                            <p className="text-lg font-medium">Aucun client trouvé</p>
                                            <p className="text-sm">
                                                {search || filter === 'inactive' ? 'Essayez de modifier vos filtres' : 'Les clients apparaîtront ici une fois des commandes payées'}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((c) => {
                                    const periodStatus = derivePeriodStatusFromCustomerOrg(c, periodStart);
                                    const avgCart = c.ordersPaidCount ? (c.totalSpentCents / 100) / c.ordersPaidCount : 0;
                                    return (
                                        <tr
                                            key={c.id}
                                            onClick={() => onSelectCustomer(c)}
                                            className={`border-b border-border cursor-pointer transition-all duration-150 ${selectedCustomerId === c.id ? 'bg-primary-10' : 'hover:bg-background-tertiary'}`}
                                        >
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-foreground">{c.fullName}</div>
                                                <div className="text-xs text-foreground-secondary">{c.email}</div>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <CustomerStatusDisplay status={periodStatus} size="sm" />
                                            </td>
                                            <td className="px-4 py-3 text-center text-sm text-foreground">
                                                {c.firstPaidAt ? formatDate(c.firstPaidAt) : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {c.lastPaidAt ? (
                                                    <Tooltip title={formatDate(c.lastPaidAt)} arrow>
                                                        <span className="text-sm text-foreground-secondary cursor-help">{formatRelativeDate(c.lastPaidAt)}</span>
                                                    </Tooltip>
                                                ) : (
                                                    <span className="text-sm text-foreground-secondary">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`inline-flex items-center justify-center min-w-[2rem] h-7 rounded-full text-sm font-semibold ${c.ordersPaidCount > 0 ? 'bg-primary-10 text-primary' : 'bg-background-tertiary text-foreground-secondary'}`}>
                                                    {c.ordersPaidCount}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right font-semibold text-foreground">
                                                {formatCurrency(c.totalSpentCents / 100)}
                                            </td>
                                            <td className="px-4 py-3 text-right text-foreground-secondary">
                                                {formatCurrency(avgCart)}
                                            </td>
                                            <td className="px-4 py-3 text-center text-sm text-foreground-secondary">
                                                {formatPurchaseFrequency(c.avgDaysBetweenPurchaseDays || undefined)}
                                            </td>
                                            <td className="px-4 py-3">
                                                <ChevronRight className="h-4 w-4 text-foreground-secondary" />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                {hasMore && !onPageChange && (
                    <div className="p-4 border-t border-border flex justify-center">
                        <button
                            type="button"
                            onClick={onLoadMore}
                            disabled={loading}
                            className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary-dark disabled:opacity-50 transition-colors"
                        >
                            {loading ? 'Chargement…' : `Charger plus (${pageLimit})`}
                        </button>
                    </div>
                )}
            </Card>
        </div>
    );
};
